import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Heart, ShoppingBag, Zap, Truck, RotateCcw, ShieldCheck } from "lucide-react";
import api, { inr, formatApiError } from "../lib/api";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Reveal, Overline } from "../components/Reveal";
import ProductCard from "../components/ProductCard";

export default function ProductPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToCart, toggleWishlist, wishlist } = useCart();
  const [p, setP] = useState(null);
  const [img, setImg] = useState(0);
  const [size, setSize] = useState(null);
  const [qty, setQty] = useState(1);
  const [related, setRelated] = useState([]);
  const [askOpen, setAskOpen] = useState(false);
  const [askMsg, setAskMsg] = useState("");
  const [askSent, setAskSent] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    setP(null); setImg(0); setSize(null); setQty(1);
    api.get(`/products/${id}`).then((r) => {
      setP(r.data);
      api.get("/products", { params: { brand: r.data.brand_slug } }).then((rr) =>
        setRelated(rr.data.filter((x) => x.product_id !== r.data.product_id).slice(0, 4))).catch(() => {});
    }).catch(() => setP(false));
  }, [id]);

  if (p === null) return <div className="py-40 text-center font-serif text-2xl text-muted-foreground">Jigel…</div>;
  if (p === false) return <div className="py-40 text-center font-serif text-3xl" data-testid="product-not-found">Piece not found. <Link to="/shop" className="text-wine underline">Back to shop</Link></div>;

  const wished = wishlist.some((w) => w.product_id === p.product_id);
  const saving = p.mrp > p.price ? Math.round(((p.mrp - p.price) / p.mrp) * 100) : 0;

  const buyNow = () => { addToCart(p, size, qty); navigate("/checkout"); };

  return (
    <div className="max-w-[1600px] mx-auto px-5 md:px-10 py-10 md:py-16" data-testid="product-page">
      <p className="text-[10px] tracking-[0.25em] uppercase text-muted-foreground mb-8">
        <Link to="/shop" className="hover:text-wine">Shop</Link> / <Link to={`/${p.gender}`} className="hover:text-wine capitalize">{p.gender}</Link> / <Link to={`/shop?category=${encodeURIComponent(p.category)}`} className="hover:text-wine">{p.category}</Link>
      </p>
      <div className="grid lg:grid-cols-2 gap-12 lg:gap-20">
        <div>
          <div className="aspect-[3/4] overflow-hidden bg-sand">
            <img src={p.images[img]} alt={p.name} className="w-full h-full object-cover" data-testid="product-main-image" />
          </div>
          {p.images.length > 1 && (
            <div className="mt-4 flex gap-3">
              {p.images.map((src, i) => (
                <button key={i} onClick={() => setImg(i)} data-testid={`product-thumb-${i}`}
                  className={`w-20 aspect-[3/4] overflow-hidden border ${img === i ? "border-ink" : "border-transparent opacity-60"}`}>
                  <img src={src} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>
        <Reveal>
          <div className="flex items-start justify-between gap-6">
            <div>
              <Link to={`/brands/${p.brand_slug}`} data-testid="product-brand" className="text-[11px] tracking-[0.3em] uppercase text-muted-foreground hover:text-wine">{p.brand}</Link>
              <h1 className="font-serif text-4xl sm:text-5xl tracking-tight mt-2" data-testid="product-name">{p.name}</h1>
            </div>
            <button onClick={() => toggleWishlist(p)} data-testid="product-wishlist-btn" aria-label="Wishlist"
              className="w-11 h-11 border border-border flex items-center justify-center hover:border-wine transition-colors">
              <Heart size={17} strokeWidth={1.5} className={wished ? "fill-wine text-wine" : ""} />
            </button>
          </div>
          {p.ask_for_price ? (
            <button onClick={() => setAskOpen(true)} data-testid="ask-for-price-btn"
              className="mt-5 border border-wine text-wine px-6 py-3.5 text-[11px] tracking-[0.25em] uppercase hover:bg-wine hover:text-ivory transition-colors">
              Ask for Price
            </button>
          ) : (
            <div className="mt-5 flex items-baseline gap-3">
              <span className="font-mono text-2xl" data-testid="product-price">{inr(p.price)}</span>
              {p.mrp > p.price && <>
                <span className="font-mono text-sm text-muted-foreground line-through" data-testid="product-mrp">{inr(p.mrp)}</span>
                <span className="text-[10px] tracking-[0.2em] uppercase bg-wine text-ivory px-2 py-1" data-testid="product-savings">Save {saving}%</span>
              </>}
            </div>
          )}

          {p.is_preloved && (
            <div className="mt-8 border border-gold/50 bg-gold/5 p-6" data-testid="preloved-block">
              <div className="flex items-center gap-3 flex-wrap">
                <span className="text-[10px] tracking-[0.25em] uppercase bg-ink text-ivory px-2 py-1">Pre-Owned</span>
                {p.grade && <Link to="/grade-guide" data-testid="product-grade" className="text-[10px] tracking-[0.25em] uppercase bg-wine text-ivory px-2 py-1 hover:bg-ink transition-colors">JIGEL Grade {p.grade}</Link>}
                {p.verified && <span className="text-[10px] tracking-[0.25em] uppercase bg-gold text-ink px-2 py-1">Verified</span>}
              </div>
              <p className="mt-4 font-serif text-xl">Condition · <span className="italic capitalize">{p.condition}</span>{p.approx_age_years != null && <span className="text-sm text-muted-foreground not-italic"> · approx. {p.approx_age_years} yr old</span>}</p>
              <p className="mt-2 text-xs text-ink/50 leading-relaxed">This item was previously owned and has been inspected by JIGEL before being offered for resale.</p>
              {p.condition_details && <p className="mt-2 text-sm text-ink/60 leading-relaxed"><span className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground block mb-1">Visible imperfections</span>{p.condition_details}</p>}
            </div>
          )}

          <div className="mt-8">
            <p className="text-[10px] tracking-[0.25em] uppercase text-muted-foreground mb-3">Size</p>
            <div className="flex flex-wrap gap-2">
              {(p.sizes || []).map((s) => (
                <button key={s} onClick={() => setSize(s)} data-testid={`size-${s}`}
                  className={`min-w-12 px-3 py-2.5 border text-sm transition-colors ${size === s ? "bg-ink text-ivory border-ink" : "border-border hover:border-ink"}`}>
                  {s}
                </button>
              ))}
            </div>
          </div>

          <div className="mt-6 flex items-center gap-4">
            <p className="text-[10px] tracking-[0.25em] uppercase text-muted-foreground">Quantity</p>
            <div className="flex items-center border border-border">
              <button onClick={() => setQty(Math.max(1, qty - 1))} data-testid="qty-minus" className="w-9 h-9 flex items-center justify-center hover:bg-sand">−</button>
              <span className="w-10 text-center font-mono text-sm" data-testid="qty-value">{qty}</span>
              <button onClick={() => setQty(Math.min(10, qty + 1))} data-testid="qty-plus" className="w-9 h-9 flex items-center justify-center hover:bg-sand">+</button>
            </div>
          </div>

          <div className="mt-8 flex gap-3">
            {p.ask_for_price ? (
              <button onClick={() => setAskOpen(true)} data-testid="ask-for-price-cta"
                className="flex-1 bg-wine text-ivory py-4 text-[11px] tracking-[0.25em] uppercase hover:bg-ink transition-colors">
                Ask for Price
              </button>
            ) : (
              <>
                <button onClick={() => addToCart(p, size, qty)} data-testid="add-to-cart-btn"
                  className="flex-1 bg-ink text-ivory py-4 text-[11px] tracking-[0.25em] uppercase flex items-center justify-center gap-3 hover:bg-wine transition-colors">
                  <ShoppingBag size={14} strokeWidth={1.5} /> Add to Cart
                </button>
                <button onClick={buyNow} data-testid="buy-now-btn"
                  className="flex-1 bg-wine text-ivory py-4 text-[11px] tracking-[0.25em] uppercase flex items-center justify-center gap-3 hover:bg-ink transition-colors">
                  <Zap size={14} strokeWidth={1.5} /> Buy Now
                </button>
              </>
            )}
          </div>
          <p className="mt-3 text-[11px] text-muted-foreground" data-testid="stock-status">
            {p.stock > 0 ? (p.stock <= 3 ? `Only ${p.stock} left in stock` : "In stock") : "Out of stock"}
          </p>

          <div className="mt-10 border-t border-border pt-8 grid grid-cols-2 gap-x-8 gap-y-4 text-sm">
            {[["Category", p.category], ["Gender", p.gender], ["Color", p.color], ["Material", p.material], ["SKU", p.sku], ["Condition", p.is_preloved ? p.condition : "New"]].map(([k, v]) => v && (
              <div key={k}><p className="text-[10px] tracking-[0.25em] uppercase text-muted-foreground">{k}</p><p className="mt-1 capitalize">{v}</p></div>
            ))}
          </div>
          {p.description && <p className="mt-8 text-ink/60 leading-relaxed text-sm" data-testid="product-description">{p.description}</p>}

          <div className="mt-10 space-y-4 text-sm text-ink/60">
            <p className="flex items-center gap-3"><Truck size={16} strokeWidth={1.5} /> Complimentary delivery across India in 3–5 business days.</p>
            <p className="flex items-center gap-3"><RotateCcw size={16} strokeWidth={1.5} /> 7-day easy returns on new items. Pre-loved pieces are final sale unless misrepresented.</p>
            <p className="flex items-center gap-3"><ShieldCheck size={16} strokeWidth={1.5} /> {p.is_preloved && p.verified ? "Authenticity checked by the Jigel team." : "Curated and quality-checked by Jigel."}</p>
          </div>
          <Overline className="mt-10 block">Pay with Cloth Cash at checkout</Overline>
          <Link to="/trade-in" data-testid="product-tradein-strip"
            className="mt-4 flex items-center justify-between border border-gold/50 bg-gold/5 px-5 py-4 group">
            <p className="text-sm text-ink/70">Have something similar you no longer wear? <span className="text-wine">Trade it in for Cloth Cash.</span></p>
            <span className="text-[10px] tracking-[0.2em] uppercase text-gold group-hover:tracking-[0.3em] transition-all">Trade-In →</span>
          </Link>
        </Reveal>
      </div>

      {askOpen && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center p-5" data-testid="ask-price-modal">
          <div className="absolute inset-0 bg-ink/50" onClick={() => setAskOpen(false)} />
          <div className="relative bg-ivory max-w-md w-full p-8">
            <p className="text-[10px] tracking-[0.25em] uppercase text-muted-foreground">Ask for Price</p>
            <h3 className="font-serif text-2xl mt-2">{p.name}</h3>
            {askSent ? (
              <p className="mt-5 text-sm text-ink/60 leading-relaxed" data-testid="ask-price-sent">
                Request received. Our team will respond with a price for this piece shortly.
              </p>
            ) : !user ? (
              <div className="mt-5">
                <p className="text-sm text-ink/60">Sign in so we can respond to your request.</p>
                <Link to={`/auth?next=/product/${p.product_id}`} data-testid="ask-price-signin"
                  className="mt-4 inline-block bg-ink text-ivory px-6 py-3.5 text-[11px] tracking-[0.25em] uppercase">Sign In</Link>
              </div>
            ) : (
              <form onSubmit={async (e) => {
                e.preventDefault();
                try {
                  await api.post("/price-requests", { product_id: p.product_id, message: askMsg });
                  setAskSent(true);
                  toast.success("Price request sent");
                } catch (err) { toast.error(formatApiError(err)); }
              }} className="mt-5 space-y-4">
                <textarea value={askMsg} onChange={(e) => setAskMsg(e.target.value)} rows={3}
                  placeholder="Any notes? Size preference, offer, questions…"
                  className="w-full bg-transparent border border-border px-4 py-3 text-sm outline-none focus:border-wine" data-testid="ask-price-message" />
                <button type="submit" data-testid="ask-price-submit"
                  className="w-full bg-ink text-ivory py-3.5 text-[11px] tracking-[0.25em] uppercase hover:bg-wine transition-colors">Send Request</button>
              </form>
            )}
            <button onClick={() => setAskOpen(false)} data-testid="ask-price-close" className="mt-4 text-[10px] tracking-[0.2em] uppercase text-muted-foreground hover:text-ink">Close</button>
          </div>
        </div>
      )}

      {related.length > 0 && (
        <section className="mt-24 md:mt-32" data-testid="related-products">
          <Reveal><Overline>More from {p.brand}</Overline>
            <h2 className="font-serif text-3xl sm:text-4xl tracking-tight mt-3">You may also consider</h2>
          </Reveal>
          <div className="mt-10 grid grid-cols-2 lg:grid-cols-4 gap-x-5 gap-y-12">
            {related.map((r, i) => <ProductCard key={r.product_id} product={r} index={i} />)}
          </div>
        </section>
      )}
    </div>
  );
}
